import GuestProps from '@interfaces/guest/GuestProps';
import { constructRedirectToParam, ProfilePage } from '@utils/profile/ProfileUtils';
import { ProfileEntityType, SearchResponseVO } from '@components/search/ProfileSearchProps';
import ProfileContentVO, { ProfileContentType } from '@interfaces/contents/ProfileContentVO';

const getBaseProfileUrl = (guestProps: GuestProps, subPath?: string): string => {
  let baseProfileUrl: string = '';
  if (guestProps.isPublicUser) {
    baseProfileUrl = guestProps.isCustomDomain ? window.location.origin : `/profile-center/${guestProps.profileId}/${guestProps.companyId}`;
    if (subPath) {
      baseProfileUrl += subPath;
    }
    if (guestProps.isVerifiedUser && guestProps.mailId) {
      baseProfileUrl += `?mailId=${encodeURIComponent(guestProps.mailId)}`;
    }
  } else {
    baseProfileUrl = '/profile-view';
    if (subPath) {
      baseProfileUrl += subPath;
    }
  }
  if (guestProps.podName) {
    baseProfileUrl += `${baseProfileUrl.includes('?') ? '&' : '?'}podName=${guestProps.podName}`;
  }
  return baseProfileUrl;
};

export const getSectionLink = (contentVO: ProfileContentVO | Partial<ProfileContentVO>, guestProps: GuestProps) => {
  let baseProfileUrl = getBaseProfileUrl(guestProps);
  baseProfileUrl += baseProfileUrl.includes('?') ? '&' : '?';
  return `${baseProfileUrl}sId=${contentVO.sectionId}`;
};

export const getSectionContentLink = (contentVO: ProfileContentVO | Partial<ProfileContentVO>, guestProps: GuestProps) => {
  let url = getBaseProfileUrl(guestProps);
  url += url.includes('?') ? '&' : '?';
  return `${url}sId=${contentVO.sectionId}&cId=${contentVO.id}`;
};

export const getCBSectionContentLink = (sId: string, guestProps: GuestProps) => {
  let url = getBaseProfileUrl(guestProps);
  url += url.includes('?') ? '&' : '?';
  return `${url}sId=${sId}`;
};

export const getFAQContentLink = (result: Pick<SearchResponseVO, 'id'>, guestProps: GuestProps) => {
  let url: string = getBaseProfileUrl(guestProps);
  url += url.includes('?') ? '&' : '?';
  return `${url}fId=${result.id}`;
};

const constructContentViewUrl = (queryParams: { [key: string]: string }, guestProps: GuestProps) => {
  const { isPublicUser } = guestProps || {};
  let url = getBaseProfileUrl(guestProps, '/preview-content');
  url += url.includes('?') ? '&' : '?';

  const qFields: string[] = Object.keys(queryParams);
  for (let i = 0, len = qFields.length; i < len; i++) {
    url += `${qFields[i]}=${queryParams[qFields[i]]}${i + 1 < len ? '&' : ''}`;
  }
  let redirectTo: string = constructRedirectToParam(isPublicUser ? ProfilePage.PUBLIC_PROFILE_VIEW : ProfilePage.PROFILE_VIEW);
  if (queryParams['prevParent'] && queryParams['prevFile']) {
    redirectTo = constructRedirectToParam(isPublicUser ? ProfilePage.PUBLIC_PREVIEW_PROJECT : ProfilePage.PREVIEW_PROJECT, false, redirectTo);
  }
  url += `&redirectTo=${redirectTo}`;

  return url;
};

export const getContentLink = (contentVO: ProfileContentVO, type: ProfileContentType, guestProps: GuestProps) => {
  const queryParams = {
    sectionId: contentVO.sectionId,
    id: contentVO.id,
    type
  };
  return constructContentViewUrl(queryParams, guestProps);
};

export const getResponseRedirectUrl = (response: SearchResponseVO, guestProps: GuestProps) => {
  const queryParams: { [key: string]: string } = {
    sectionId: response.usedProfiles[0].sectionId,
    id: response.usedProfiles[0].id
  };
  switch (response.entityType) {
    case ProfileEntityType.PD:
      queryParams['type'] = ProfileContentType.DOCUMENT;
      break;
    case ProfileEntityType.PQ:
      queryParams['type'] = ProfileContentType.PROJECT;
      break;
    case ProfileEntityType.PQ_SECTION:
      queryParams['type'] = ProfileContentType.PROJECT;
      queryParams['currentSection'] = response.id;
      break;
    case ProfileEntityType.PQ_QA:
      queryParams['type'] = ProfileContentType.PROJECT;
      queryParams['currentSection'] = response.sectionId;
      queryParams['qId'] = response.id;
      break;
    case ProfileEntityType.PQ_ATTACHMENT:
      queryParams['type'] = ProfileContentType.PROJECT;
      queryParams['currentSection'] = response.attachedEntity[0].sectionId;
      queryParams['prevParent'] = response.attachedEntity[0].questionId || response.attachedEntity[0].sectionId;
      queryParams['prevFile'] = response.fileId;
      break;
    default:
      return '';
  }

  return constructContentViewUrl(queryParams, guestProps);
};

export const getPQSectionLink = (contentVO: ProfileContentVO, qSId: string, guestProps: GuestProps) => {
  const queryParams = {
    sectionId: contentVO.sectionId,
    id: contentVO.id,
    type: ProfileContentType.PROJECT,
    currentSection: qSId
  };
  return constructContentViewUrl(queryParams, guestProps);
};

export const getPQQuestionLink = (contentVO: ProfileContentVO, questionId: string, qSId: string, guestProps: GuestProps) => {
  const queryParams = {
    sectionId: contentVO.sectionId,
    id: contentVO.id,
    type: ProfileContentType.PROJECT,
    currentSection: qSId,
    qId: questionId
  };
  return constructContentViewUrl(queryParams, guestProps);
};
